import * as vfsClient from './vfsClient';
import indexedDbFileStorage from './indexedDbFileStorage';
import { devLog } from './devLog';

// `${projectId}:${serviceName}` pairs already force-resynced this page
// load — module-scoped (not per-component-instance) so Canvas.jsx and
// VfsChatPanel.jsx (which can both trigger a resync for the same project,
// from different mounted trees) share one idempotency record instead of
// each doing its own redundant network round-trip.
const resyncedServices = new Set();

/**
 * Explicit, eager vfs-service -> IndexedDB reconciliation. Until the user
 * opens Monaco for a file the first time, vfs-service is the one master —
 * this pulls its current state and overwrites whatever IndexedDB has,
 * rather than trusting a possibly-stale local copy (e.g. the scaffold
 * placeholder written at project-creation time, before the agent's real
 * code existed). Call this at every point a stale IndexedDB copy would
 * otherwise be shown, or worse, get pushed BACK onto vfs-service as if it
 * were a real edit (see MonacoFileEditor.jsx's isApplyingExternalUpdateRef
 * fix for that half of this same bug class) — today: the first "Refresh"
 * click in Code mode, and the moment vfs_get_preview_url completes in Chat.
 *
 * Idempotent and safe to call from multiple places/components — a given
 * (projectId, serviceName) pair only actually syncs once per page load;
 * every other caller's await just resolves once that first sync (or a
 * later explicit forceRefresh) has completed. AFTER that first sync, the
 * WS live-sync effect (Canvas.jsx's connectProjectTreeSync) and the normal
 * IndexedDB<->vfs-service two-way flow take over — vfs-service is no
 * longer treated as the sole master from that point on.
 *
 * @param {string} projectId
 * @param {string} serviceName
 * @param {{forceRefresh?: boolean}} [options] - forceRefresh re-syncs even
 *   if this pair was already synced this page load (used by "Refresh").
 * @returns {Promise<boolean>} whether the sync ran (false = skipped: not
 *   vfs-backed, missing args, or already synced without forceRefresh).
 */
export async function forceResyncServiceFromVfs(projectId, serviceName, options = {}) {
  if (!projectId || !serviceName || !vfsClient.isVfsBackedProject(projectId)) return false;
  const key = `${projectId}:${serviceName}`;
  if (resyncedServices.has(key) && !options.forceRefresh) return false;
  resyncedServices.add(key);
  try {
    const [vfsFiles, localFiles] = await Promise.all([
      vfsClient.fetchServiceFiles(projectId, serviceName),
      indexedDbFileStorage.getAllFiles(projectId, serviceName),
    ]);
    const localByPath = new Map(localFiles.map((f) => [f.filePath, f.content]));
    let changed = false;
    for (const [filePath, content] of Object.entries(vfsFiles)) {
      if (localByPath.get(filePath) !== content) {
        // eslint-disable-next-line no-await-in-loop
        await indexedDbFileStorage.saveFile(projectId, serviceName, filePath, content, { fromVfs: true });
        changed = true;
      }
      localByPath.delete(filePath);
    }
    // Gone from vfs-service: dropped locally too — except a file with unsaved local work (NEW/MODIFIED), which is
    // never thrown away by a sync.
    const dirty = new Set(localFiles.filter((f) => f.gitState === 'new' || f.gitState === 'modified').map((f) => f.filePath));
    for (const filePath of localByPath.keys()) {
      if (dirty.has(filePath) || vfsClient.isSyncExcludedPath(`${serviceName}/${filePath}`)) { continue; }
      // eslint-disable-next-line no-await-in-loop
      await indexedDbFileStorage.deleteFile(projectId, serviceName, filePath);
      changed = true;
    }
    // eslint-disable-next-line no-console
    devLog('🔄 forceResyncServiceFromVfs: done', { projectId, serviceName, changed, fileCount: Object.keys(vfsFiles).length });
    if (changed) {
      window.dispatchEvent(new CustomEvent('gitPullCompleted', {
        detail: { projectId, serviceName, fromAutoRender: false },
      }));
      window.dispatchEvent(new CustomEvent('vfsForceResyncApplied', {
        detail: { projectId, serviceName },
      }));
    }
    return true;
  } catch (error) {
    // Allow a retry later rather than permanently marking this pair as done.
    resyncedServices.delete(key);
    console.warn('⚠️ forceResyncServiceFromVfs failed:', { projectId, serviceName, error });
    throw error;
  }
}

export default forceResyncServiceFromVfs;
